import type { CustomerAdminDetail } from '@avrahami/contracts';
import {
  Alert,
  Button,
  Card,
  ConfirmDialog,
  formatDate,
  formatDateTime,
  Loading,
  PageHeader,
  returnToPrevious,
} from '@avrahami/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { useState } from 'react';
import { useCan } from '../api/session';
import { customerQuery, keys, tenantApi, useStoreTimeZone } from '../api/tenant';
import { CustomerStatusBadge } from '../components/status';
import { he } from '../i18n/he';
import { errorMessage } from '../lib/forms';
import { QuoteRequestsTable } from './QuoteRequestsPage';

export function CustomerDetailPage() {
  const { customerId } = useParams({ from: '/app/customers/$customerId' });
  const customer = useQuery(customerQuery(customerId));
  const canUpdate = useCan('store.customers.update');
  const timeZone = useStoreTimeZone();

  if (customer.isPending) return <Loading />;
  if (customer.isError) return <Alert>{errorMessage(customer.error)}</Alert>;
  const detail = customer.data;

  return (
    <>
      <Link
        to="/customers"
        onClick={returnToPrevious}
        className="mb-2 inline-block text-sm text-brand-700 hover:underline"
      >
        {he.customers.back}
      </Link>
      <PageHeader
        title={detail.fullName}
        actions={canUpdate ? <StatusToggle customer={detail} /> : null}
      />
      <div className="flex flex-col gap-6">
        <Card title={he.customers.details}>
          <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-sm">
            <dt className="text-slate-500">{he.customers.status}</dt>
            <dd>
              <CustomerStatusBadge status={detail.status} />
            </dd>
            <dt className="text-slate-500">{he.customers.email}</dt>
            <dd className="ltr">{detail.email}</dd>
            <dt className="text-slate-500">{he.customers.phone}</dt>
            <dd className="ltr">{detail.phone ?? he.common.none}</dd>
            <dt className="text-slate-500">{he.customers.orders}</dt>
            <dd>{detail.orderCount}</dd>
            <dt className="text-slate-500">{he.customers.joined}</dt>
            <dd>{formatDate(detail.createdAt, timeZone)}</dd>
            <dt className="text-slate-500">{he.customers.lastLogin}</dt>
            <dd>
              {detail.lastLoginAt === null
                ? he.common.none
                : formatDateTime(detail.lastLoginAt, timeZone)}
            </dd>
          </dl>
        </Card>
        <Card title={he.customers.customerOrders}>
          {detail.orders.length === 0 ? (
            <p className="text-sm text-slate-500">{he.customers.noOrders}</p>
          ) : (
            <QuoteRequestsTable orders={detail.orders} />
          )}
        </Card>
      </div>
    </>
  );
}

/** Disable or enable the shopper's account, after a confirmation. */
function StatusToggle({ customer }: { customer: CustomerAdminDetail }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const disabling = customer.status === 'ACTIVE';
  const change = useMutation({
    mutationFn: () =>
      tenantApi.updateCustomer(customer.id, {
        version: customer.version,
        status: disabling ? 'DISABLED' : 'ACTIVE',
      }),
    onSuccess: async () => {
      setOpen(false);
      await queryClient.invalidateQueries({ queryKey: keys.customers() });
    },
  });
  return (
    <>
      <Button
        tone={disabling ? 'danger' : 'primary'}
        data-testid="customer-status-toggle"
        onClick={() => {
          change.reset();
          setOpen(true);
        }}
      >
        {disabling ? he.customers.disable : he.customers.enable}
      </Button>
      <ConfirmDialog
        open={open}
        title={disabling ? he.customers.disableTitle : he.customers.enableTitle}
        confirmLabel={disabling ? he.customers.disable : he.customers.enable}
        tone={disabling ? 'danger' : 'primary'}
        busy={change.isPending}
        onConfirm={() => {
          change.mutate();
        }}
        onClose={() => {
          setOpen(false);
        }}
      >
        <p>{disabling ? he.customers.disableBody : he.customers.enableBody}</p>
        {change.isError ? (
          <div className="mt-3">
            <Alert>{errorMessage(change.error)}</Alert>
          </div>
        ) : null}
      </ConfirmDialog>
    </>
  );
}
